<template>
  <section class="live-monitor">
    <b-card no-body class="live-header-card mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-start justify-content-between">
          <div>
            <h4 class="mb-0">Live Connection Monitor</h4>
            <small class="text-muted">Real-time bandwidth & session stats</small>
          </div>
          <div class="header-meta">
            <span :class="['session-badge', sessionBadgeVariant]">
              {{ sessionBadgeLabel }}
            </span>
            <span :class="['status-pill', statusVariant]">
              <span class="pulse-dot" />
              {{ statusLabel }}
            </span>
            <small class="text-muted ml-1">Updated {{ lastUpdatedLabel }}</small>
          </div>
        </div>
        <div class="refresh-bar mt-1">
          <b-form-checkbox v-model="autoRefresh" switch class="mr-1">
            Auto refresh
          </b-form-checkbox>
          <b-form-select
            v-model="refreshInterval"
            :options="refreshOptions"
            size="sm"
            class="mr-1 refresh-select"
            :disabled="!autoRefresh"
          />
          <b-button size="sm" variant="primary" :disabled="isRefreshing" @click="manualRefresh">
            <span v-if="isRefreshing" class="spinner-border spinner-border-sm mr-50" />
            Refresh now
          </b-button>
          <small class="text-muted ml-1">Effective: {{ effectiveIntervalLabel }}</small>
        </div>
      </b-card-body>
    </b-card>

    <div class="live-alerts" v-if="highUsageWarning || zeroTrafficWarning">
      <b-alert v-if="highUsageWarning" variant="warning" show class="mb-1">
        High usage detected ({{ downloadLabel }} / {{ uploadLabel }}).
      </b-alert>
      <b-alert v-if="zeroTrafficWarning" variant="danger" show class="mb-0">
        Zero traffic for {{ zeroTrafficStreak }} refreshes. Check router or user device.
      </b-alert>
    </div>

    <b-alert v-if="loadError" variant="danger" show class="mb-2">
      {{ loadError }}
    </b-alert>

    <div v-if="isInitialLoading" class="live-skeleton">
      <div class="skeleton-line skeleton-line--lg"></div>
      <div class="skeleton-grid">
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line"></div>
      </div>
    </div>

    <b-row v-else>
      <b-col lg="8">
        <b-card no-body class="live-hero-card mb-2">
          <b-card-body>
            <div class="hero-grid">
              <div class="hero-stat">
                <small class="text-muted">Live Download</small>
                <div class="hero-value">{{ downloadLabel }}</div>
                <div class="sparkline">
                  <apexchart
                    type="line"
                    height="70"
                    :options="sparklineOptionsDownload"
                    :series="downloadSparkSeries"
                  />
                </div>
                <small class="text-muted">Updated {{ liveUpdatedLabel }}</small>
              </div>
              <div class="hero-stat">
                <small class="text-muted">Live Upload</small>
                <div class="hero-value hero-value--alt">{{ uploadLabel }}</div>
                <div class="sparkline">
                  <apexchart
                    type="line"
                    height="70"
                    :options="sparklineOptionsUpload"
                    :series="uploadSparkSeries"
                  />
                </div>
                <small class="text-muted">Updated {{ liveUpdatedLabel }}</small>
              </div>
              <div class="hero-status">
                <div class="hero-pulse"></div>
                <div class="text-muted">Live signal</div>
                <div class="trend-line">
                  <span class="trend-arrow" :class="trendVariant">{{ trendArrow }}</span>
                  <small class="text-muted">{{ trendLabel }}</small>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <b-card
          no-body
          class="mb-2 live-graph-card"
          @touchstart="onGraphTouchStart"
          @touchend="onGraphTouchEnd"
        >
          <b-card-body>
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
              <div>
                <h5 class="mb-0">Live Bandwidth Graph</h5>
                <small class="text-muted">Last {{ chartSeries[0].data.length }} samples</small>
              </div>
              <div class="graph-actions">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  :disabled="!historyRows.length"
                  @click="exportCsv"
                >
                  CSV
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  :disabled="!chartSeries[0].data.length"
                  @click="exportPng"
                >
                  PNG
                </b-button>
                <b-button v-if="isMobileView" size="sm" variant="primary" @click="toggleGraph">
                  {{ graphCollapsed ? 'Show Graph' : 'Hide Graph' }}
                </b-button>
              </div>
            </div>
            <div v-show="!graphCollapsed" class="graph-wrapper">
              <apexchart ref="liveChart" type="area" height="260" :options="chartOptions" :series="chartSeries" />
            </div>
            <div v-if="graphCollapsed" class="graph-collapsed-hint">
              Swipe up to expand live graph
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col lg="4">
        <b-card no-body class="mb-2">
          <b-card-body>
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="mb-0">Connection</h5>
              <span :class="['session-badge', sessionBadgeVariant]">{{ sessionBadgeLabel }}</span>
            </div>
            <div class="info-row">
              <span class="text-muted">PPP Username</span>
              <strong>{{ usernameInfo.user_name || '-' }}</strong>
            </div>
            <div class="info-row">
              <span class="text-muted">Package</span>
              <strong>{{ packageName }}</strong>
            </div>
            <div class="info-row">
              <span class="text-muted">Package Price</span>
              <strong>{{ packagePrice }}</strong>
            </div>
            <div class="info-row">
              <span class="text-muted">Last App Login</span>
              <strong>{{ usernameInfo.last_app_hit || '-' }}</strong>
            </div>
            <div class="info-row">
              <span class="text-muted">Customer</span>
              <strong>{{ userName }}</strong>
            </div>
            <div class="info-row">
              <span class="text-muted">Mobile</span>
              <strong>{{ userMobile }}</strong>
            </div>
          </b-card-body>
        </b-card>

        <b-card no-body class="mb-2 qos-card">
          <b-card-body>
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="mb-0">Live QoS</h5>
              <span :class="['qos-chip', qosVariant]">
                {{ qosLabel }}
                <span class="trend-arrow" :class="trendVariant">{{ trendArrow }}</span>
              </span>
            </div>
            <div class="qos-grid">
              <div class="qos-item">
                <small class="text-muted">Ping</small>
                <div class="qos-value">--</div>
                <small class="text-muted">ms</small>
              </div>
              <div class="qos-item">
                <small class="text-muted">Jitter</small>
                <div class="qos-value">--</div>
                <small class="text-muted">ms</small>
              </div>
              <div class="qos-item">
                <small class="text-muted">Packet Loss</small>
                <div class="qos-value">--</div>
                <small class="text-muted">%</small>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <b-card no-body>
          <b-card-body>
            <h5 class="mb-1">Session Stats</h5>
            <div class="stat-grid">
              <div>
                <small class="text-muted">Uptime</small>
                <div class="stat-value">{{ staticdata.connection_running || '-' }}</div>
              </div>
              <div>
                <small class="text-muted">Total Download</small>
                <div class="stat-value">{{ staticdata.total_download || '-' }}</div>
              </div>
              <div>
                <small class="text-muted">Total Upload</small>
                <div class="stat-value">{{ staticdata.total_upload || '-' }}</div>
              </div>
              <div>
                <small class="text-muted">Last Connected</small>
                <div class="stat-value">{{ staticdata.connection_established_at || '-' }}</div>
              </div>
              <div>
                <small class="text-muted">Last Disconnected</small>
                <div class="stat-value" :class="{ 'disconnect-recent': lastDisconnectIsRecent }">
                  {{ staticdata.last_disconnect_date_time || '-' }}
                </div>
              </div>
              <div>
                <small class="text-muted">MAC</small>
                <div class="stat-value">{{ staticdata.mac_address || '-' }}</div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-card no-body class="mt-2" v-if="historyRows.length">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h5 class="mb-0">Recent Snapshots</h5>
          <small class="text-muted">Last {{ historyRows.length }}</small>
        </div>
        <vue-good-table
          :columns="historyColumns"
          :rows="historyRows"
          :search-options="{ enabled: true }"
          :pagination-options="{ enabled: true, perPage: 6 }"
          style-class="vgt-table condensed"
        />
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import { BCard, BCardBody, BRow, BCol, BButton, BFormCheckbox, BFormSelect, BAlert } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { VueGoodTable } from 'vue-good-table'
import {
  USERNAME_VIEW,
  LIVE_DATA,
  STATIC_DATA,
} from '@core/services/api'
import router from '@/router'
import Mixin from '@core/services/mixin'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormCheckbox,
    BFormSelect,
    BAlert,
    apexchart: VueApexCharts,
    VueGoodTable,
  },
  mixins: [Mixin],
  data() {
    return {
      usernameId: router.currentRoute.params.usernameId,
      userData: null,
      usernameInfo: {},
      livedata: {},
      staticdata: {},
      isInitialLoading: true,
      isRefreshing: false,
      isLiveLoading: false,
      isStaticLoading: false,
      loadError: null,
      liveStatus: 'idle',
      lastLiveAt: null,
      lastStaticAt: null,
      autoRefresh: true,
      refreshInterval: 5000,
      slowRefreshInterval: 15000,
      usageThresholdKbps: 50000,
      zeroTrafficStreak: 0,
      refreshOptions: [
        { value: 3000, text: '3s' },
        { value: 5000, text: '5s' },
        { value: 10000, text: '10s' },
      ],
      refreshTimer: null,
      activeRefreshInterval: null,
      chartLabels: [],
      chartSeries: [
        { name: 'Download', data: [] },
        { name: 'Upload', data: [] },
      ],
      chartOptions: {
        chart: {
          toolbar: { show: false },
          sparkline: { enabled: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
        },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        dataLabels: { enabled: false },
        xaxis: { categories: [] },
        yaxis: { labels: { formatter: val => `${(val / 1000).toFixed(2)} Mbps` } },
        colors: ['#00cfe8', '#f6b73c'],
        tooltip: {
          theme: 'light',
          y: { formatter: val => `${(val / 1000).toFixed(2)} Mbps` },
        },
        grid: { borderColor: '#eef0f3' },
      },
      sparklineOptionsDownload: {
        chart: { sparkline: { enabled: true }, animations: { enabled: true, speed: 400 } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { opacity: 0.2 },
        dataLabels: { enabled: false },
        tooltip: { enabled: false },
        colors: ['#00cfe8'],
      },
      sparklineOptionsUpload: {
        chart: { sparkline: { enabled: true }, animations: { enabled: true, speed: 400 } },
        stroke: { curve: 'smooth', width: 2 },
        fill: { opacity: 0.2 },
        dataLabels: { enabled: false },
        tooltip: { enabled: false },
        colors: ['#f6b73c'],
      },
      historyRows: [],
      historyColumns: [
        { label: 'Time', field: 'time', sortable: true },
        { label: 'Download', field: 'download', sortable: true },
        { label: 'Upload', field: 'upload', sortable: true },
      ],
      graphCollapsed: false,
      isMobileView: false,
      touchStartY: null,
    }
  },
  computed: {
    statusLabel() {
      if (this.liveStatus === 'online') return 'Online'
      if (this.liveStatus === 'offline') return 'Offline'
      if (this.liveStatus === 'disconnected') return 'Disconnected'
      return 'Idle'
    },
    statusVariant() {
      if (this.liveStatus === 'online') return 'status-pill--online'
      if (this.liveStatus === 'offline') return 'status-pill--offline'
      if (this.liveStatus === 'disconnected') return 'status-pill--disconnected'
      return 'status-pill--idle'
    },
    userName() {
      return (this.userData && this.userData.name) ? this.userData.name : '-'
    },
    userMobile() {
      return (this.userData && this.userData.mobile) ? this.userData.mobile : '-'
    },
    packageName() {
      return this.usernameInfo.package_name
        || (this.usernameInfo.package && this.usernameInfo.package.package_name)
        || '-'
    },
    packagePrice() {
      const price = this.usernameInfo.package_price
        || this.usernameInfo.billing_price
        || (this.usernameInfo.package && this.usernameInfo.package.price)
      return price || '-'
    },
    downloadLabel() {
      return this.livedata.current_download || '--'
    },
    uploadLabel() {
      return this.livedata.current_upload || '--'
    },
    lastUpdatedLabel() {
      return this.formatRelativeTime(this.lastLiveAt || this.lastStaticAt)
    },
    liveUpdatedLabel() {
      return this.formatRelativeTime(this.lastLiveAt)
    },
    downloadSparkSeries() {
      return [{ name: 'Download', data: this.chartSeries[0].data.slice(-20) }]
    },
    uploadSparkSeries() {
      return [{ name: 'Upload', data: this.chartSeries[1].data.slice(-20) }]
    },
    latestDownloadKbps() {
      const data = this.chartSeries[0].data
      return data.length ? data[data.length - 1] : 0
    },
    latestUploadKbps() {
      const data = this.chartSeries[1].data
      return data.length ? data[data.length - 1] : 0
    },
    highUsageWarning() {
      if (this.liveStatus !== 'online') return false
      return Math.max(this.latestDownloadKbps, this.latestUploadKbps) >= this.usageThresholdKbps
    },
    zeroTrafficWarning() {
      if (this.liveStatus !== 'online') return false
      return this.zeroTrafficStreak >= 3
    },
    sessionActive() {
      return this.liveStatus === 'online' || Boolean(this.staticdata.connection_running)
    },
    sessionBadgeLabel() {
      return this.sessionActive ? 'Session Active' : 'Session Inactive'
    },
    sessionBadgeVariant() {
      return this.sessionActive ? 'session-badge--active' : 'session-badge--inactive'
    },
    trafficTrend() {
      const down = this.chartSeries[0].data
      const up = this.chartSeries[1].data
      if (down.length < 2 || up.length < 2) return 'flat'
      const last = down[down.length - 1] + up[up.length - 1]
      const prev = down[down.length - 2] + up[up.length - 2]
      if (last > prev * 1.1) return 'up'
      if (last < prev * 0.9) return 'down'
      return 'flat'
    },
    trendArrow() {
      if (this.trafficTrend === 'up') return '^'
      if (this.trafficTrend === 'down') return 'v'
      return '-'
    },
    trendLabel() {
      if (this.trafficTrend === 'up') return 'Rising'
      if (this.trafficTrend === 'down') return 'Falling'
      return 'Stable'
    },
    trendVariant() {
      if (this.trafficTrend === 'up') return 'trend-arrow--up'
      if (this.trafficTrend === 'down') return 'trend-arrow--down'
      return 'trend-arrow--flat'
    },
    qosLabel() {
      if (this.liveStatus === 'disconnected') return 'No Signal'
      if (this.highUsageWarning) return 'Busy'
      if (this.liveStatus === 'offline') return 'Idle'
      return 'Stable'
    },
    qosVariant() {
      if (this.liveStatus === 'disconnected') return 'qos-chip--bad'
      if (this.highUsageWarning) return 'qos-chip--warn'
      if (this.liveStatus === 'offline') return 'qos-chip--idle'
      return 'qos-chip--ok'
    },
    effectiveInterval() {
      return this.liveStatus === 'disconnected' ? this.slowRefreshInterval : this.refreshInterval
    },
    effectiveIntervalLabel() {
      return this.formatInterval(this.effectiveInterval)
    },
    lastDisconnectIsRecent() {
      const parsed = this.parseRouterTimestamp(this.staticdata.last_disconnect_date_time)
      if (!parsed) return false
      return (Date.now() - parsed.getTime()) <= (10 * 60 * 1000)
    },
  },
  mounted() {
    this.setViewportState()
    this.loadInitial()
    this.startAutoRefresh()
    document.addEventListener('visibilitychange', this.handleVisibility)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    document.removeEventListener('visibilitychange', this.handleVisibility)
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    autoRefresh() {
      this.startAutoRefresh()
    },
    refreshInterval() {
      this.startAutoRefresh()
    },
    liveStatus() {
      if (this.autoRefresh && this.activeRefreshInterval !== this.effectiveInterval) {
        this.startAutoRefresh()
      }
    },
  },
  methods: {
    setViewportState() {
      const wasMobile = this.isMobileView
      this.isMobileView = window.innerWidth < 992
      if (!this.isMobileView) {
        this.graphCollapsed = false
      } else if (!wasMobile) {
        this.graphCollapsed = true
      }
    },
    handleResize() {
      this.setViewportState()
    },
    toggleGraph() {
      this.graphCollapsed = !this.graphCollapsed
    },
    onGraphTouchStart(event) {
      if (!this.isMobileView) return
      if (!event.changedTouches || !event.changedTouches.length) return
      this.touchStartY = event.changedTouches[0].clientY
    },
    onGraphTouchEnd(event) {
      if (!this.isMobileView || this.touchStartY === null) return
      if (!event.changedTouches || !event.changedTouches.length) return
      const delta = this.touchStartY - event.changedTouches[0].clientY
      if (delta > 40) this.graphCollapsed = false
      if (delta < -40) this.graphCollapsed = true
      this.touchStartY = null
    },
    async loadInitial() {
      this.isInitialLoading = true
      this.loadError = null
      try {
        await Promise.all([this.getUserNameData(), this.getLiveData(), this.getStaticData()])
      } catch (error) {
        this.loadError = 'Failed to load live data.'
      } finally {
        this.isInitialLoading = false
      }
    },
    async manualRefresh() {
      this.isRefreshing = true
      await Promise.all([this.getLiveData(true), this.getStaticData(true)])
      this.isRefreshing = false
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      if (!this.autoRefresh) return
      this.activeRefreshInterval = this.effectiveInterval
      this.refreshTimer = setInterval(() => {
        if (document.hidden) return
        this.getLiveData(true)
        if (!this.lastStaticAt || (Date.now() - this.lastStaticAt.getTime() > 30000)) {
          this.getStaticData(true)
        }
      }, Number(this.activeRefreshInterval))
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    handleVisibility() {
      if (document.hidden) {
        this.stopAutoRefresh()
        return
      }
      if (this.autoRefresh && !this.refreshTimer) {
        this.startAutoRefresh()
      }
    },
    getUserNameData() {
      return this.$http
        .get(process.env.VUE_APP_BASEURL + USERNAME_VIEW + this.usernameId)
        .then(res => {
          const data = res.data.data || {}
          this.usernameInfo = data
          this.userData = data.users || null
        })
    },
    getLiveData(silent = false) {
      if (this.isLiveLoading) return Promise.resolve()
      this.isLiveLoading = true
      if (!silent) this.loadError = null
      return this.$http
        .get(process.env.VUE_APP_BASEURL + LIVE_DATA + this.usernameId)
        .then(res => {
          const data = res.data.data || {}
          this.livedata = data
          this.liveStatus = data.status === true ? 'online' : 'offline'
          this.lastLiveAt = new Date()
          this.appendSample()
        })
        .catch(() => {
          this.liveStatus = 'disconnected'
        })
        .finally(() => {
          this.isLiveLoading = false
        })
    },
    getStaticData(silent = false) {
      if (this.isStaticLoading) return Promise.resolve()
      this.isStaticLoading = true
      if (!silent) this.loadError = null
      return this.$http
        .get(process.env.VUE_APP_BASEURL + STATIC_DATA + this.usernameId)
        .then(res => {
          this.staticdata = res.data.data || {}
          this.lastStaticAt = new Date()
        })
        .catch(() => {
          this.staticdata = {}
        })
        .finally(() => {
          this.isStaticLoading = false
        })
    },
    appendSample() {
      const timestamp = new Date()
      const downloadValue = this.parseBandwidth(this.livedata.current_download)
      const uploadValue = this.parseBandwidth(this.livedata.current_upload)
      if (downloadValue === null || uploadValue === null) return

      const isZeroTraffic = downloadValue <= 0.1 && uploadValue <= 0.1
      this.zeroTrafficStreak = isZeroTraffic ? this.zeroTrafficStreak + 1 : 0

      this.chartLabels.push(this.formatTime(timestamp))
      this.chartSeries[0].data.push(downloadValue)
      this.chartSeries[1].data.push(uploadValue)

      if (this.chartLabels.length > 60) {
        this.chartLabels.shift()
        this.chartSeries[0].data.shift()
        this.chartSeries[1].data.shift()
      }

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: { ...this.chartOptions.xaxis, categories: [...this.chartLabels] },
      }

      const row = {
        time: this.formatTime(timestamp),
        download: this.livedata.current_download || '-',
        upload: this.livedata.current_upload || '-',
      }
      this.historyRows = [row, ...this.historyRows].slice(0, 30)
    },
    exportCsv() {
      if (!this.historyRows.length) return
      const header = ['time', 'download', 'upload'].join(',')
      const lines = this.historyRows.map(row => [
        row.time,
        row.download,
        row.upload,
      ].join(','))
      const content = [header, ...lines].join('\n')
      this.downloadBlob(content, `live-snapshots-${this.formatFileStamp(new Date())}.csv`, 'text/csv')
    },
    exportPng() {
      if (!this.$refs.liveChart || !this.$refs.liveChart.dataURI) return
      this.$refs.liveChart.dataURI().then(({ imgURI }) => {
        this.downloadUrl(imgURI, `live-bandwidth-${this.formatFileStamp(new Date())}.png`)
      })
    },
    downloadBlob(content, filename, type) {
      const blob = new Blob([content], { type })
      const url = window.URL.createObjectURL(blob)
      this.downloadUrl(url, filename)
      window.URL.revokeObjectURL(url)
    },
    downloadUrl(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
    },
    formatFileStamp(dateValue) {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
      return date.toISOString().replace(/[:.]/g, '-')
    },
    parseBandwidth(value) {
      if (!value || typeof value !== 'string') return null
      const match = value.match(/([\d.]+)\s*([A-Za-z]+)/)
      if (!match) return null
      const num = Number(match[1])
      const unit = match[2].toLowerCase()
      if (Number.isNaN(num)) return null
      const multipliers = {
        b: 1 / 1000,
        kb: 1,
        mb: 1000,
        gb: 1000 * 1000,
        kib: 1.024,
        mib: 1024,
        gib: 1024 * 1024,
      }
      const scale = multipliers[unit] || 1
      return Number((num * scale).toFixed(2))
    },
    formatRelativeTime(dateValue) {
      if (!dateValue) return 'never'
      const diff = Math.max(0, Math.floor((Date.now() - dateValue.getTime()) / 1000))
      if (diff < 5) return 'just now'
      if (diff < 60) return `${diff}s ago`
      const minutes = Math.floor(diff / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ago`
    },
    formatTime(dateValue) {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    formatInterval(ms) {
      return `${Math.round(ms / 1000)}s`
    },
    parseRouterTimestamp(value) {
      if (!value || typeof value !== 'string') return null
      const parsed = Date.parse(value)
      if (!Number.isNaN(parsed)) return new Date(parsed)
      const match = value.match(/^([a-z]{3})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})/i)
      if (!match) return null
      const monthMap = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      }
      const month = monthMap[match[1].toLowerCase()]
      if (month === undefined) return null
      return new Date(
        Number(match[3]),
        month,
        Number(match[2]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6]),
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-good-table.scss";

.live-monitor {
  --live-accent: #00cfe8;
  --live-accent-alt: #f6b73c;
  --live-glow: rgba(0, 207, 232, 0.2);
}

.live-header-card {
  border: 1px solid rgba(0, 207, 232, 0.2);
  background: linear-gradient(135deg, #f8fbff 0%, #fff6e6 100%);
}

.header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.refresh-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.refresh-select {
  min-width: 80px;
}

.live-alerts {
  margin-bottom: 0.75rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #f0f0f0;
}

.status-pill--online {
  background: rgba(0, 207, 232, 0.15);
  color: #029aa9;
}

.status-pill--offline {
  background: rgba(255, 177, 0, 0.15);
  color: #c47c00;
}

.status-pill--disconnected {
  background: rgba(234, 84, 85, 0.15);
  color: #ea5455;
}

.status-pill--idle {
  background: rgba(113, 102, 240, 0.12);
  color: #5e50ee;
}

.session-badge {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.session-badge--active {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
  box-shadow: 0 0 12px rgba(34, 197, 94, 0.25);
}

.session-badge--inactive {
  background: rgba(234, 84, 85, 0.15);
  color: #ea5455;
  box-shadow: 0 0 12px rgba(234, 84, 85, 0.2);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.2);
  animation: pulse 1.6s infinite;
}

.live-hero-card {
  background: #ffffff;
  border: 1px solid rgba(0, 207, 232, 0.25);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.hero-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  align-items: center;
}

.hero-stat {
  padding: 0.25rem 0.5rem;
}

.hero-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 0 16px var(--live-glow);
}

.hero-value--alt {
  color: #f6b73c;
}

.hero-status {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.trend-line {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.trend-arrow {
  font-weight: 700;
}

.trend-arrow--up {
  color: #16a34a;
}

.trend-arrow--down {
  color: #ea5455;
}

.trend-arrow--flat {
  color: #64748b;
}

.sparkline {
  min-height: 70px;
}

.hero-pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--live-accent);
  box-shadow: 0 0 12px var(--live-accent);
  animation: pulse 1.2s infinite;
}

.graph-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.graph-wrapper {
  transition: all 0.2s ease;
}

.graph-collapsed-hint {
  padding: 0.75rem;
  border: 1px dashed rgba(100, 116, 139, 0.4);
  border-radius: 0.75rem;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
}

.qos-card {
  border: 1px solid rgba(0, 207, 232, 0.15);
}

.qos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.qos-item {
  background: #f8fafc;
  border-radius: 0.65rem;
  padding: 0.5rem;
  text-align: center;
}

.qos-value {
  font-weight: 700;
  font-size: 1.1rem;
  color: #0f172a;
}

.qos-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.qos-chip--ok {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.qos-chip--warn {
  background: rgba(255, 177, 0, 0.2);
  color: #c47c00;
}

.qos-chip--idle {
  background: rgba(148, 163, 184, 0.2);
  color: #475569;
}

.qos-chip--bad {
  background: rgba(234, 84, 85, 0.15);
  color: #ea5455;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
  border-bottom: 1px dashed #e5e7eb;
}

.info-row:last-child {
  border-bottom: 0;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}

.stat-value {
  font-weight: 600;
}

.disconnect-recent {
  color: #ea5455;
  font-weight: 700;
}

.live-skeleton {
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid #e6e8eb;
  background: #fff;
  margin-bottom: 1rem;
}

.skeleton-line {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: skeleton-move 1.4s ease-in-out infinite;
  margin-bottom: 0.75rem;
}

.skeleton-line--lg {
  height: 18px;
  width: 60%;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}

@media (max-width: 767.98px) {
  .hero-grid {
    grid-template-columns: 1fr;
  }

  .hero-value {
    font-size: 1.6rem;
  }

  .graph-actions {
    width: 100%;
  }

  .qos-grid {
    grid-template-columns: 1fr;
  }

  .live-monitor .btn {
    min-height: 44px;
  }
}

@keyframes skeleton-move {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes pulse {
  0% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(0, 207, 232, 0.4); }
  70% { transform: scale(1); box-shadow: 0 0 0 12px rgba(0, 207, 232, 0); }
  100% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(0, 207, 232, 0); }
}
</style>
